import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { copilotApi } from '@/services/copilot';
import styles from './InfographicGeneratorModal.module.css';

interface InfographicGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  articleTitle: string;
  onInsertInfographic: (infographicUrl: string) => void;
}

export function InfographicGeneratorModal({
  isOpen,
  onClose,
  articleTitle,
  onInsertInfographic,
}: InfographicGeneratorModalProps) {
  const { t } = useTranslation(['common', 'nav', 'board', 'articles', 'analytics', 'feeddrive', 'config', 'strategist']);
  void t;
  const [headlineOverlay, setHeadlineOverlay] = useState(articleTitle);
  const [brandSlug, setBrandSlug] = useState('wiredesk');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewResult, setPreviewResult] = useState<{ width?: number; height?: number; bytes?: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      const res = await copilotApi.generateInfographic({
        title: articleTitle,
        headline_overlay: headlineOverlay,
        brand_slug: brandSlug,
      });

      if (res.ok) {
        setPreviewResult({ width: res.width, height: res.height, bytes: res.size_bytes });
      } else {
        setError(res.error || 'Falha ao gerar infográfico.');
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <h3>Gerador de Infográfico / Card Estatístico (BrandKit)</h3>
          <button className={styles.closeBtn} onClick={onClose}>Fechar
          </button>
        </div>

        <div className={styles.modalBody}>
          {/* F-382 - generate_infographic RENDERIZA o PNG e o descarta: a
              resposta traz so {ok,width,height,format,size_bytes}, sem bytes e
              sem url (backend/app/services/copilot_service.py:392-398). O que a
              insercao colocava na materia era o literal
              `[Infografico Renderizado: ...]`, que ia para o WordPress como
              texto entre colchetes. Enquanto a imagem nao voltar, isto e dito
              na cara do operador - antes de ele gastar a geracao. */}
          <div
            style={{
              background: 'var(--sig-warn-bg)',
              border: '1px solid var(--sig-warn-border)',
              borderRadius: 'var(--r-md)',
              color: 'var(--sig-warn-text)',
              padding: '12px 14px',
              fontSize: 'var(--fs-base)',
              lineHeight: 1.45,
            }}
          >
            <strong style={{ display: 'block', marginBottom: '4px' }}>
              Ainda não conectado
            </strong>
            O motor renderiza a peça no servidor, mas a imagem não volta para o painel:
            só as medidas voltam. A inserção colocaria um MARCADOR DE TEXTO na
            matéria — a imagem não entra no artigo nem chega ao WordPress (em
            construção).
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Texto da Headline / Destaque Gráfico:</label>

            <textarea
              className={styles.textarea}
              rows={3}
              value={headlineOverlay}
              onChange={(e) => setHeadlineOverlay(e.target.value)}
              placeholder="Digite a frase/dado estatístico a ser queimado no card..."
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Marca / Portal (BrandKit):</label>
            <select
              className={styles.select}
              value={brandSlug}
              onChange={(e) => setBrandSlug(e.target.value)}
            >
              <option value="wiredesk">Padrão</option>
              <option value="ncnews">NC News</option>
              <option value="portal">Portal Geral</option>
            </select>
          </div>

          {error && <div className={styles.errorMessage}>{error}</div>}

          {previewResult && (
            <div
              style={{
                background: 'var(--bg-2)',
                border: '1px solid var(--border-2)',
                borderRadius: 'var(--r-md)',
                color: 'var(--text-2)',
                padding: '12px 14px',
                fontSize: 'var(--fs-base)',
                lineHeight: 1.45,
              }}
            >
              <strong style={{ display: 'block', marginBottom: '4px', color: 'var(--text-1)' }}>
                Peça montada no servidor — e ela parou lá
              </strong>
              Medidas devolvidas: {previewResult.width}x{previewResult.height}px, PNG
              {previewResult.bytes ? `, ${Math.round(previewResult.bytes / 1024)} KB` : ''}. O
              arquivo não é devolvido ao painel, então não há imagem para inserir na
              matéria (em construção).
            </div>
          )}
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.cancelBtn} onClick={onClose}>Cancelar
          </button>

          {!previewResult ? (
            <button
              className={styles.generateBtn}
              onClick={handleGenerate}
              disabled={isGenerating || !headlineOverlay.trim()}
            >
              {isGenerating ? ' Gerando Peça no Motor...' : ' Gerar Infográfico em 1-Clique'}
            </button>
          ) : (
            // Desligado de proposito: o clique inseria `[Infografico
            // Renderizado: ...]` no corpo, e esse colchete sairia publicado no
            // WordPress no lugar da imagem. O onClick continua escrito para o
            // dia em que o motor devolver a URL - ligar de volta e trocar o
            // `disabled` fixo pela existencia da url na resposta.
            <button
              className={styles.cancelBtn}
              style={{ opacity: 0.6, cursor: 'not-allowed' }}
              disabled
              title="Desligado enquanto a imagem não volta do motor: inserir hoje colocaria só um marcador de texto na matéria, e ele sairia publicado no lugar do infográfico (em construção)."
              onClick={() => {
                onInsertInfographic(`[Infográfico Renderizado: ${headlineOverlay}]`);
                onClose();
              }}
            >Inserir no artigo (indisponível)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
